import { Geometry, HorizontalAlign, Log, LogGroup, Point, Rect, Size, VerticalAlign } from ".././MyHelpers";
import { ExpandingWildReelConfiguration, ExpandingWildReelResult, PlayWindow } from "../api";
import { AudioManager, Track } from "../AudioManager";
import { ISystemContext } from "../Context/SystemContext";
import { Resourceful } from "../ResourceManager/Resourceful";
import { ResourceIdentifier } from "../ResourceManager/ResourceIdentifier";
import { IReadOnlyResourceManager, IWriteOnlyResourceManager } from "../ResourceManager/ResourceManager";
import { SpriteResource } from "../ResourceManager/SpriteResource";
import { Settings } from "../Settings";
import { ZIndex } from "../ZIndex";
import { ExpandingWildPosition } from "./ExpandingWildBulk";
import { SlotFeature } from "./SlotFeature";
import { SlotSymbol } from "./SlotSymbol";
import { ReelSetMapper, ReelSetMapperOverlay } from "./SpinAnimation/ReelSetMapper";
import { SymbolTypeAnim } from "./SpineSymbols/SymbolsCollection";
import { CanvasSprite } from "./Sprite/CanvasSprite";
import { ISprite } from "./Sprite/ISprite";

export type ExpandingWildReelOnShowCallback = (wildIndex: number) => void;
export class ExpandingWildReel
{
	// size and position of this expanding wild
	//private rect: PixelRect = null;

	// size of the wild, in symbol unites
	public symbolsRect: Rect = null;

	private wildIndex: number
	private expandingWildSymbolIndex: number;

	private reelSetMapper: ReelSetMapper = null;

	private sprite: CanvasSprite = null;

	constructor(wildIndex: number, expandingWildSymbolIndex: number, sprite: CanvasSprite, symbolsRect: Rect, reelSetMapper: ReelSetMapper) {
		this.wildIndex = wildIndex;
		this.expandingWildSymbolIndex = expandingWildSymbolIndex;
		this.sprite = sprite;
		this.symbolsRect = symbolsRect;
		this.reelSetMapper = reelSetMapper;

		this.sprite.View().style.position = "absolute";
		this.sprite.View().style.visibility = "hidden"; // default hidden
	}

	public SetRect(symbolsRect: Rect, pixelsRect: Rect): void {
		this.symbolsRect = symbolsRect;

		this.sprite.SetSize(pixelsRect.Size());
		this.sprite.View().style.left = pixelsRect.x + "px";
		this.sprite.View().style.top = pixelsRect.y + "px";

        this.sprite.AddOnCompleteCallback(() => {
            this.Hide();
        });
	}

	public View(): HTMLElement {
		return this.sprite.View();
	}

    protected visible: boolean = false;

	public Show(): void {



        this.visible = true;

		// TODO zoom in wild
		this.sprite.View().style.visibility = "visible";
		this.sprite.Restart(false);

		// after wild is shown, replace symbols in the background on reels to the wild symbols
		this.Apply();

        if (this.visible)
        {
            return;
        }
		this.onShowCallback?.(this.wildIndex);
	}

	public Apply(): void
	{
		this.reelSetMapper.AddOverlay(new ReelSetMapperOverlay(this.symbolsRect, this.expandingWildSymbolIndex));
	}

	public Hide(): void {

		this.sprite.View().style.visibility = "hidden";
		this.sprite.Stop();

        if (!this.visible)
        {
            return;
        }

        this.visible = false;
        this.onHideCallback?.(this.wildIndex);
	}

	public Update(): void {
		this.sprite.Update();
	}

    public onShowCallback: ((wildIndex: number) => void) = null;
    public onHideCallback: ((wildIndex: number) => void) = null;
}



export type ExpandingWildsReelFinishedCallback = () => void;
export type ExpandingWildsReelNextWildCallback = (wildIndex: number, rect: Rect) => void;

export interface ExpandingWildsReelParam
{
	readonly resid: ResourceIdentifier;
	readonly logGroup: LogGroup;
	readonly resourcesBasePath: string;
	readonly slotWindow: Rect;
	readonly slotWindowSymbolSize: PlayWindow;
	readonly reelSetMapper: ReelSetMapper;
	readonly audio: AudioManager;
	readonly gameName: string;
    readonly availableSymbols: SlotSymbol[];
	readonly systemContext: ISystemContext;
	readonly symbolTypeAnim : SymbolTypeAnim;
}

export class ExpandingWildsReel extends SlotFeature implements Resourceful
{
	protected params: ExpandingWildsReelParam = null;

	// size of slot window, in pixels
	private slotWindow: Rect = null;

	// size of slot window, in symbols
	private slotWindowSymbolSize: PlayWindow = null;

	// size of single symbol, in pixels
	private symbolSize: Size = null;

	private sprite: ISprite = null;
	//private canvas: MyCanvas = null;
	private view: HTMLElement = null;

	private currentWild: number = -1;
	//private timer: number = 0;

	private reelSetMapper: ReelSetMapper = null;
    private symbol: SlotSymbol = null;

	private audio: AudioManager = null;

	private systemContext: ISystemContext;

	// private nextWildDelay: IInterval;

	public constructor(param: ExpandingWildsReelParam, config: ExpandingWildReelConfiguration) {
		super(param.logGroup, param.resid, "ExpandingWildsReel", param.resourcesBasePath);

		this.params = param;

		this.symbol = param.availableSymbols.find(s => s.ID == config.Symbol);

        if (!this.symbol)
            throw `No required symbol ${config.Symbol}`;
		this.slotWindow = param.slotWindow;
		this.slotWindowSymbolSize = param.slotWindowSymbolSize;
		this.reelSetMapper = param.reelSetMapper;

		if (this.reelSetMapper == null) {
			Log.Error(this.logGroup, "ReelSetMapper should not be null");
			debugger;
		}

		this.symbolSize = new Size(this.slotWindow.width / this.slotWindowSymbolSize.Width, this.slotWindow.height / this.slotWindowSymbolSize.Height);

		this.audio = param.audio;

		this.view = document.createElement("div");
		this.view.classList.add(this.resid.Scope());
		this.view.style.zIndex=ZIndex.WildBulkReel.toString();

		this.systemContext = param.systemContext.CreateSubContext("ExpandingWildsBulk");

		// this.nextWildDelay = this.systemContext.TimingContext().CreateInterval("nextWildDelay", 4000, () => {
		// 	this.NextWild();
		// });
	}

	public wilds: ExpandingWildReel[] = [];
	private lastResult: ExpandingWildReelResult = null;

	public async PrepareResources(resourceManager: IWriteOnlyResourceManager): Promise<void> {
		// load sprite
		resourceManager.AddGlobal(new SpriteResource(this.resourcesBasePath + "expanding_wild_reel", ResourceIdentifier.Global.Add(this.params.gameName).Add("Feature").Add("ExpandingWildReel"), true, this.logGroup.Add("ExpandingWildReel"), true));

		// load sound
		// TODO don't we play sound in parent class - freegame or basegame class?
		this.audio.AddTrack(new Track(this.resourcesBasePath + "background" + Settings.audioFormat, this.resid.ScopedResourceName("background")));
	}

	public async ResourcesLoaded(resourceManager: IReadOnlyResourceManager): Promise<void> {
		this.sprite = resourceManager.Get<SpriteResource>(ResourceIdentifier.Global.Add(this.params.gameName).Add("Feature").Add("ExpandingWildReel")).sprite;
	}

	public Prepare(featureResult: any): void 
	{
		let result: ExpandingWildReelResult = featureResult as ExpandingWildReelResult;
		this.lastResult = result;

		// create additional wilds
		if (this.wilds.length < result.Positions.length)
		{
			for (let i = this.wilds.length; i < result.Positions.length; i++) {


				let sprite: CanvasSprite = new CanvasSprite({sprite: this.sprite.Copy(), canvasSize: new Size(1, 1)});
				let wild: ExpandingWildReel = new ExpandingWildReel(i, this.symbol.Index, sprite, new Rect(0, 0, 1, 1), this.reelSetMapper);
				wild.View().style.zIndex=ZIndex.WildBulk.toString();
				//wild.AddOnShowCallback((wildIndex: number) => this.OnWildShown(wildIndex));
                wild.onShowCallback = this.OnWildShown.bind(this);
                wild.onHideCallback = this.OnWildHidden.bind(this);

				this.wilds.push(wild);
				this.view.appendChild(wild.View());
			}
		}

		// wild expands to all neighbours
		for (let i = 0; i < result.Positions.length; i++) {
			let position: ExpandingWildPosition = result.Positions[i];

	        let reel = position.reel;

	        let topSymbol = 0
	        let bottomSymbol = this.slotWindowSymbolSize.Height - 1;

			// include right and bottom symbols, they are covered
			let wildSymbolsRect = Rect.FromCoordiantes(reel, topSymbol, reel +1, bottomSymbol +1);

			let wildRect = new Rect(
				(wildSymbolsRect.x * this.symbolSize.Width) + this.slotWindow.x,
				(wildSymbolsRect.y * this.symbolSize.Height) + this.slotWindow.y,

				wildSymbolsRect.width * this.symbolSize.Width,
				wildSymbolsRect.height * this.symbolSize.Height
			);

			// calculate actual size, so that image covers the symbols that will change

			// remember center, wild zooms in from the center symbol, that triggered
			let centerSymbol = new Point(position.reel, position.row);
			// center, in pixels
			//let center = new Point((centerSymbol.x + 0.5) * this.symbolSize.width, (centerSymbol.y + 0.5) * this.symbolSize.height);

			let wild: ExpandingWildReel = this.wilds[i];

			// calculate size of canvas, so that it covers whole area 
			let wildCoverRect = Geometry.CoverRect(this.sprite.CurrentFrameRect, wildRect, HorizontalAlign.Center, VerticalAlign.Center);
			let wildCoverInflatedRect = wildCoverRect.Inflate(new Size(wildCoverRect.width * 0.1, wildCoverRect.height * 0.1));

			// set new position and size of the wild
			wild.SetRect(wildSymbolsRect, wildCoverInflatedRect);
		}
	}

	protected started: boolean = false;
	protected stopRequested: boolean = false;

	public get IsRunning(): boolean
	{
		return this.started;
	}

	public Start(): void 
	{
		if (!this.started)
		{
			this.started = true;
			this.stopRequested = false;

			this.currentWild = -1;
			//this.nextWildDelay.Start();
			
			// start first wild right now
			this.NextWild();
		}
	}

	public RequestStop(): void
	{
		if (this.started)
		{
            this.started = false;

			if (!this.stopRequested)
			{
				Log.Error(this.logGroup, "Requesting stop!");
				this.stopRequested = true;
				// stop timer if running
				//this.nextWildDelay.Stop();

				// hide current wild
				if (this.currentWild != -1) 
				{
					this.wilds[this.currentWild].Hide();

					// make sure to redraw sprites
					this.wilds[this.currentWild].Update();
				}

				// go fast trough remaining wilds
				for (this.currentWild++; this.currentWild <this.lastResult.Positions.length; this.currentWild++)
				{
					// only apply wild without showing it
					this.wilds[this.currentWild].Apply();

					this.wilds[this.currentWild].Update();

					// move to next
					this.OnNext(this.currentWild, this.wilds[this.currentWild].symbolsRect);
				}
				this.StopAudio();
				// reset current wild
				this.currentWild = -1;

				this.OnComplete();
			}
			else
			{
				Log.Error(this.logGroup, "Stop already requested!");
			}
		}
		else
		{
			Log.Error(this.logGroup, "Feature not started!");
		}
	}

	private OnWildShown(wildIndex: number)
    {
		this.sprite.Restart(false);
	}

    private OnWildHidden(wildIndex: number)
    {
        this.NextWild();
    }

	private NextWild(): void
    {
        if (!this.started) return;

		if (this.currentWild >= this.lastResult.Positions.length -1) {

			// hide the last
			if (this.currentWild != -1) {
				this.wilds[this.currentWild].Hide();
			}
			
			// finished
			// Log.Debug(this.logGroup, "Wilds complete");

			//this.nextWildDelay.Stop();
			this.started = false;
			this.stopRequested = false;
			this.OnComplete();
		}
		else
        {

			if (this.currentWild != -1) {
				// hide current wild
				this.wilds[this.currentWild].Hide();
			}

			this.currentWild++;

			// Log.Debug(this.logGroup, "Displaying wild " + this.currentWild + " of " + this.lastResult.Positions.length);


			this.wilds[this.currentWild].Show();

			// play audio - repeat up to 3 times depending on wild size
			//let wildArea: number = this.wilds[this.currentWild].symbolsRect.Area();
			//let repeats: number = 1;

			// Log.Debug(this.logGroup, "Expanding wild area " + wildArea + ", repeats " + repeats);

			this.audio.Play(this.resid.ScopedResourceName("background"));
			this.OnNext(this.currentWild, this.wilds[this.currentWild].symbolsRect);
		}
	}

	protected StopAudio(): void
	{
		// then also stop audio
		this.audio.Stop(this.resid.ScopedResourceName("background"));
	}

	public Update() {
		if (this.currentWild != -1) {
			this.wilds[this.currentWild].Update();
		}
	}



	public View(): HTMLElement {
		return this.view;
	}

	private completeCallbaks: ExpandingWildsReelFinishedCallback[] = [];
	private OnComplete() {

        //// Log.Debug(this.logGroup, "OnComplete()")
		for (let callback of this.completeCallbaks) {
			callback();
		}
	}

	public AddOnCompleteCallback(callback: ExpandingWildsReelFinishedCallback) {
		this.completeCallbaks.push(callback);
	}

	private nextWildCallbacks: ExpandingWildsReelNextWildCallback[] = [];
	private OnNext(wildIndex: number, rect: Rect) {
		for (let callback of this.nextWildCallbacks) {
			callback(wildIndex, rect);
		}
	}

	// public AddOnNextCallback(callback: ExpandingWildsReelNextWildCallback) {
	// 	this.nextWildCallbacks.push(callback);
	// }
}